/**
 * Presets de grille Puck — KEEP IN SYNC avec
 * inventozen-frontpublic/src/cms-puck/grid-presets.ts
 */
import type { ColumnWidthWeight } from "@/lib/cms-puck/layout";
import { cmsNew } from "@/lib/cms/cms-new-i18n";

export type GridPreset =
  | "custom"
  | "equal-2"
  | "equal-3"
  | "equal-4"
  | "sidebar-left"
  | "sidebar-right"
  | "bento"
  | "two-plus-one";

export function getGridPresetOptions(): ReadonlyArray<{
  label: string;
  value: GridPreset;
}> {
  return [
    { label: cmsNew("cms.new.options.gridPreset.custom", "Personnalisé"), value: "custom" },
    { label: cmsNew("cms.new.options.gridPreset.equal2", "2 colonnes égales"), value: "equal-2" },
    { label: cmsNew("cms.new.options.gridPreset.equal3", "3 colonnes égales"), value: "equal-3" },
    { label: cmsNew("cms.new.options.gridPreset.equal4", "4 colonnes égales"), value: "equal-4" },
    { label: cmsNew("cms.new.options.gridPreset.sidebarLeft", "Sidebar gauche (1/3)"), value: "sidebar-left" },
    { label: cmsNew("cms.new.options.gridPreset.sidebarRight", "Sidebar droite (1/3)"), value: "sidebar-right" },
    { label: cmsNew("cms.new.options.gridPreset.bento", "Bento (1 large + 2)"), value: "bento" },
    { label: cmsNew("cms.new.options.gridPreset.twoPlusOne", "2 + 1 (pleine largeur)"), value: "two-plus-one" },
  ];
}

const GRID_PRESET_VALUES = [
  "custom",
  "equal-2",
  "equal-3",
  "equal-4",
  "sidebar-left",
  "sidebar-right",
  "bento",
  "two-plus-one",
] as const satisfies readonly GridPreset[];


export function normalizeGridPreset(value: unknown): GridPreset {
  const v = String(value ?? "");
  return (GRID_PRESET_VALUES as readonly string[]).includes(v)
    ? (v as GridPreset)
    : "custom";
}

/** Applique un preset → colonnes + largeurs. `null` si preset custom. */
export function applyGridPreset(preset: GridPreset): {
  columns: number;
  columnWidths: ColumnWidthWeight[];
} | null {
  switch (preset) {
    case "equal-2":
      return { columns: 2, columnWidths: ["1", "1"] };
    case "equal-3":
      return { columns: 3, columnWidths: ["1", "1", "1"] };
    case "equal-4":
      return { columns: 4, columnWidths: ["1", "1", "1", "1"] };
    case "sidebar-left":
      return { columns: 2, columnWidths: ["1", "3"] };
    case "sidebar-right":
      return { columns: 2, columnWidths: ["3", "1"] };
    case "bento":
      return { columns: 3, columnWidths: ["2", "1", "1"] };
    case "two-plus-one":
      return { columns: 2, columnWidths: ["1", "1"] };
    default:
      return null;
  }
}

/** CSS pour le preset « 2+1 » : le 3ᵉ enfant occupe toute la largeur. */
export function gridPresetSpanCss(
  scopeClass: string,
  preset: GridPreset,
): string {
  if (preset !== "two-plus-one") return "";
  const bo = `.${scopeClass} .cms-grid-bridge [data-puck-dropzone] > [data-puck-component]:nth-child(3)`;
  const front = `.${scopeClass} .cms-grid-bridge > *:nth-child(3)`;
  return `${bo},${front}{grid-column:1/-1}`;
}
